import { Contract, JsonRpcProvider, ethers } from "ethers";
import {
    AggregatorAbi,
    TAPIResponseWithAbi,
    TChainName,
    TCustomError,
    TFragment,
    TOutputType,
    TParsedFragment,
    TestContractAbi,
    supportedChains
} from "../types";

export const coder = ethers.AbiCoder.defaultAbiCoder();

/**
* Deserialises a raw byte string into a tuple of numbers
* @param bytes a bytestring representing a tuple
* @param types an array of types to decode
* @returns an array of decoded numbers
*/
export function decodeRawBytes(bytes: string, types: string[]): any[] {
    if (bytes.length != 64 * types.length + 2) {
        throw Error("The bytes length does not match the number of types");
    }

    let results: any[] = [];
    const step = 64; // 32 bytes
    let start = 2; // '0x' prefix

    for (let i = 0; i < types.length; i++) {

        const stop = start + step;
        let value: string = bytes.slice(start, stop);

        switch (types[i]) {
            case 'address':
                results[i] = coder.decode([types[i]], `0x${value}`)[0];
                break;
            case 'bool':
                results[i] = Boolean(coder.decode([types[i]], `0x${value}`));
                break;
            default:
                results[i] = Number(coder.decode([types[i]], `0x${value}`));
        }

        start = stop;
    }

    return results;
}

export function decodeSignedInteger(num: string): number {
    return decodeRawBytes(num, ['int256'])[0];
}

/**
 * Serializes the given params into a raw byte string
 * @param types an array of types to use for encoding
 * @param params an array of params matching the positions of types
 * @returns A raw byte string
 */
export function encodeCallParams(types: string[], params: string[]): string {
    // Encode params to raw bytes
    return coder.encode(types, params);
}

export function extractFragmentOutputs(fragment: TFragment): TOutputType[] {
    let outputs: TOutputType[] = [];

    if (fragment && fragment.outputs && fragment.outputs.length) {
        for (const output of fragment.outputs) {
            outputs.push({
                name: output.name,
                type: output.type
            });
        }
    }

    return outputs;
}

export function extractInputTypesfromFragment(fragment: TFragment): string[] {
    let types: string[] = [];
    if (fragment && fragment.inputs && fragment.inputs.length) {
        for (const type of fragment.inputs) {
            types.push(type.type);
        }
    }
    return types;
}

export function getAggregatorContract(chainName: TChainName): Contract {
    const provider: JsonRpcProvider = getProvider(chainName);
    const address: string = supportedChains[chainName].gcr;
    if (!address) { throw new Error(`No contract address for chain: ${chainName}`) };
    return getContract(address, AggregatorAbi, provider);
}

export function getProvider(chainName: TChainName): JsonRpcProvider {
    const url: string = supportedChains[chainName].rpc;
    // Populate a JSON RPC provider with a valid RPC URL
    const provider: JsonRpcProvider = new ethers.JsonRpcProvider(url);
    return provider;
}

export function getContract(address: string, abi: any, provider: JsonRpcProvider): Contract {
    // Populate a Contract object with actual values
    const contract: Contract = new ethers.Contract(address, abi, provider);
    return contract;
}

export async function getContractAbi(chainName: TChainName, address: string): Promise<TAPIResponseWithAbi> {
    try {

        const chain = supportedChains[chainName];

        console.log('Chain:', chain)

        const apiUrl: string = chain.api;
        // It will work without API Key too, but only 1 request / 5 seconds
        const apiKey: string = chain.apiKey;
        console.log("apiKey", apiKey)

        const url = `${apiUrl}?module=contract&action=getabi&address=${address}&apikey=${apiKey}`;

        const response = await fetch(url);

        if (response && response.status) {
            const data = await response.json();
            console.log(data)
            const abi = await JSON.parse(data.result);
            return { abi, error: '' };
        } else {
            return { abi: [], error: "API request failed" }
        }

    } catch (error: TCustomError) {
        return { abi: [], error: error.message }
    }

}

export function getEmptyFunctionCallParams(): string {
    // Produce a valid empty bytes array
    return coder.encode(['bytes'], [new Uint8Array()]);
}

export function getFunctionSelector(name: string, params: string[]) {
    // Compute bytes4(keccak256(funcName(...types)))
    return ethers.FunctionFragment.getSelector(name, params);
}

export function isPositiveInteger(num: number): boolean {
    // Check if the number is an integer and is >= 0
    return Number.isInteger(num) && num >= 0;
}

export function isValidEvmAddress(address: string): boolean {
    // Regular expression for EVM address format (hexadecimal, 40 characters)
    const regex = /^(0x)?[0-9a-fA-F]{40}$/;

    // Test the address against the regular expression
    return regex.test(address);
}

export function isValidSelector(selector: string): boolean {
    // Regular expression for a valid bytes4 function selector (hexadecimal, 8 characters)
    const regex = /^0x[a-fA-F0-9]{8}$/;

    // Test the selector against the regular expression
    return regex.test(selector);
}

export async function readSettingsFromFile(): Promise<any> {
    try {
        const [fileHandle] = await (window as any).showOpenFilePicker();
        const file = await fileHandle.getFile();
        const contents = await file.text();
        return JSON.parse(contents);
    } catch (err) {
        console.error(`Error reading a JSON file:`, err);
        return null;
    }
}

export function parseAbi(abi: any): TParsedFragment[] {

    const fragments: TParsedFragment[] = [];

    if (abi.length) {

        for (const fragment of abi) {

            const types: string[] = extractInputTypesfromFragment(fragment);

            if (fragment && fragment.name && types) {
                const name: string = fragment.name;

                fragments.push({
                    name,
                    selector: getFunctionSelector(name, types),
                    types,
                    outputs: extractFragmentOutputs(fragment)
                });
            }

        }

    }

    return fragments;

}

export async function saveSettingsToFile(settings: Object): Promise<void> {
    try {
        const fileHandle = await (window as any).showSaveFilePicker();
        const writableStream = await fileHandle.createWritable();
        await writableStream.write(JSON.stringify(settings));
        await writableStream.close();
    } catch (err) {
        console.error('Error saving settings:', err);
    }
}

export function stringToStringArray(input: string): string[] {
    // Split the input string by commas
    const parts = input.split(',');
    return parts;
}